import {Box, Divider, Skeleton} from 'native-base';
import {H3Text, H5Text, H6Text} from '../../../../components/core/text';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {isWeb, sendSMS} from '../../../../utils/common';
import {
  useColorThemeType,
  useFontTheme,
} from '../../../../appStyles/hooks/theme';
import {
  useFetchMaskedNumber,
  useGetBuyerInfoByOrderId,
} from '../../../hooks/ItemDetails';

import {Analytics} from '@buncha/config/analytics/analytics';
import BottomSheet from '../../../../components/composites/bottomSheet';
import {Buyer} from '../../../../driver/types/common';
import {Buyers} from '../../../types/itemDetails';
import Call from '@buncha/components/core/icon/Call';
import ChatIcon from '@buncha/components/core/icon/ChatIcon';
import {ClientType} from '@buncha/common/types/common';
import {CommonScreens} from '@buncha/common/navigation/types';
import {EventType} from '@buncha/config/analytics/type';
import {HBox} from '../../../../components/core/view';
import If from '../../../../components/conditional/If';
import {Linking} from 'react-native';
import {MainScreens} from '@buncha/navigation/types';
import {Touchable} from '@buncha/components/core/touchable';
import {UserTypes} from '@buncha/onBoarding/types';
import {navigation} from '@buncha/config/navigation';
import {styles} from './style';
import {useRoute} from '@react-navigation/native';

interface ChatBottomsheetProps {
  buyerInfo?: Buyers | Buyer | {firstName: string; phoneNumber: number};
  isOpen: boolean;
  onClose: () => void;
  orderItemId?: number;
  orderId?: number;
  runId?: number;
  storeId?: number;
  phoneNumber?: string | undefined;
  name?: string | undefined;
  isB2BStore?: boolean;
}

interface RouteParamsType {
  storeIndex?: string;
  catalogItemId?: number;
  orderItemId?: number;
}

const ChatBottomsheet: React.FC<ChatBottomsheetProps> = props => {
  const theme = useColorThemeType('main');
  const mortise = useFontTheme('mortise');
  const muli = useFontTheme('muli');
  const route = useRoute();
  const routeParams: RouteParamsType | undefined = route?.params;
  const orderItemId = routeParams?.orderItemId || props?.orderItemId;

  const {fetchMaskedNumber, info} = useFetchMaskedNumber();
  const {buyerInfo, runId, orderId, isOpen, onClose, isB2BStore} = props;

  const {buyerDetail, buyerLoading, getBuyerInfo, buyerChatInfo} =
    useGetBuyerInfoByOrderId();
  let firstName = buyerInfo?.firstName || buyerDetail?.firstName;
  const lastName = buyerDetail?.lastName;

  const [phoneNumber, setPhoneNumber] = useState(
    Number(buyerInfo?.phoneNumber || buyerDetail?.phoneNumber),
  );

  const getNumber = useCallback(async () => {
    await fetchMaskedNumber({
      runId: runId || 0,
      orderId: orderId || 0,
    });
  }, [fetchMaskedNumber, orderId, runId]);

  const contactCustomer = useCallback(() => {
    Linking.openURL(`tel:${phoneNumber}`);
  }, [phoneNumber]);

  useEffect(() => {
    getNumber();
    Analytics.eventWithProps('Contact Bottom Sheet Oepn', {
      type: EventType.cta,
      client: ClientType.meijer,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (info?.data?.fetchMaskedNumber)
      setPhoneNumber(Number(info?.data?.fetchMaskedNumber));
    else
      setPhoneNumber(
        Number(buyerInfo?.phoneNumber || buyerDetail?.phoneNumber),
      );
  }, [buyerDetail?.phoneNumber, buyerInfo?.phoneNumber, info?.data]);

  const sendChatMessage = useCallback(async () => {
    if (buyerChatInfo?.conversationId && buyerChatInfo?.isVisible && !isWeb()) {
      props.onClose();
      navigation.navigate(MainScreens.Common, {
        screen: CommonScreens.Chat,
        params: {
          hideRole: UserTypes.driver,
          conversationId: buyerChatInfo?.conversationId,
          name: `${firstName || ''} ${lastName?.[0] || ''}`,
          phoneNumber,
          runId: props?.runId,
        },
      });
    } else {
      await sendSMS(phoneNumber ?? props?.phoneNumber ?? '', '');
    }
  }, [
    buyerChatInfo?.conversationId,
    buyerChatInfo?.isVisible,
    firstName,
    lastName,
    phoneNumber,
    props,
  ]);

  useEffect(() => {
    if (!orderId) return;
    getBuyerInfo(runId || 0, orderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderItemId]);

  const getSubTitleSubfix = useMemo(() => {
    if (isB2BStore) return 'SMS';
    return 'chat';
  }, [isB2BStore]);

  return (
    <BottomSheet open={isOpen} onClose={onClose}>
      <Box style={[styles.parentBox, styles.parentBoxMeijer]}>
        <HBox>
          <H3Text
            style={[
              styles.textAlign,
              styles.header,
              {
                fontFamily: mortise?.[700]?.normal,
                color: theme?.text?.blue?.[300],
              },
            ]}>
            Contact{' '}
            <If condition={!buyerLoading}>{props?.name || firstName}</If>
            <If condition={buyerLoading}>
              <Skeleton style={styles.skeleton} />
            </If>
          </H3Text>
        </HBox>
        <H6Text
          style={[
            styles.textAlign,
            styles.subtitleText,
            {
              fontFamily: muli?.[700]?.normal,
              color: theme?.text?.gray?.[100],
            },
          ]}>
          Connect with your customers via {getSubTitleSubfix}
        </H6Text>
        <Divider style={styles.divider} />
        <Touchable onPress={sendChatMessage} style={styles.messageContainer}>
          <ChatIcon />
          <H5Text style={styles.messageText}>
            {`Message ${props?.name || firstName || ''}`}{' '}
          </H5Text>
        </Touchable>
        <Divider style={styles.optionsDivider} />
        <Touchable onPress={contactCustomer} style={styles.messageContainer}>
          <Call viewBox="0 0 25 20" fill={theme?.icon?.blue?.[300]} />
          <H5Text style={styles.messageText}>
            {`Call ${props?.name || firstName || ''}`}{' '}
          </H5Text>
        </Touchable>
      </Box>
    </BottomSheet>
  );
};

export default React.memo(ChatBottomsheet);
